import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { outletService } from '../../services/outletService';
import warehouseService from '../../services/warehouseService';
import { CreateOutletDTO } from '../../types';
import { ArrowLeft, Store, MapPin, Receipt, Lock } from 'lucide-react';
import '../../modules/restaurant/pages/RestaurantPage.css';

import { getServerErrorMessage, parseApiError } from '../../utils/errorUtils';
interface WarehouseOption { id: number; name: string; code?: string; }

interface FormData {
  code: string;
  name: string;
  description: string;
  address: string;
  phone: string;
  email: string;
  warehouseId: number | '';
  serviceChargePercent: number;
  deliveryCharge: number;
  dayCloseCutoff: string;
  isActive: boolean;
}

const OutletCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Set when the outlet cap is hit — shows an upgrade panel instead of a plain error.
  const [limitReached, setLimitReached] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    code: '',
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    warehouseId: '',
    serviceChargePercent: 0,
    deliveryCharge: 0,
    dayCloseCutoff: '',
    isActive: true,
  });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  useEffect(() => {
    warehouseService
      .getWarehouses(1, 100)
      .then((res) => {
        const data = res.data?.data ?? [];
        setWarehouses(
          Array.isArray(data)
            ? data.map((w: any) => ({ id: w.id, name: w.name, code: w.code }))
            : []
        );
      })
      .catch(() => {/* non-fatal */});

    if (isEdit && id) {
      setLoadingData(true);
      outletService
        .getById(parseInt(id))
        .then((res) => {
          const d = (res.data as any)?.data ?? res.data;
          setFormData({
            code: d.code ?? '',
            name: d.name ?? '',
            description: d.description ?? '',
            address: d.address ?? '',
            phone: d.phone ?? '',
            email: d.email ?? '',
            warehouseId: d.warehouseId ?? '',
            serviceChargePercent: d.serviceChargePercent ?? 0,
            deliveryCharge: d.deliveryCharge ?? 0,
            dayCloseCutoff: d.dayCloseCutoff ?? '',
            isActive: d.isActive ?? true,
          });
        })
        .catch(() => setError(t('restaurant.outlet.failedToLoad')))
        .finally(() => setLoadingData(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    let finalValue: string | number | boolean = type === 'checkbox' ? checked : value;
    if (name === 'code') finalValue = (value as string).toUpperCase();
    if (name === 'warehouseId') finalValue = value === '' ? '' : parseInt(value);
    if (name === 'serviceChargePercent' || name === 'deliveryCharge') finalValue = value === '' ? 0 : parseFloat(value);
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (fieldErrors[name as keyof FormData]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const errs: Partial<Record<keyof FormData, string>> = {};
    if (!formData.code.trim()) errs.code = t('common.required');
    if (!formData.name.trim()) errs.name = t('common.required');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError(null);
    setLimitReached(null);
    const payload: CreateOutletDTO = {
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      address: formData.address.trim() || undefined,
      phone: formData.phone.trim() || undefined,
      email: formData.email.trim() || undefined,
      warehouseId: formData.warehouseId !== '' ? (formData.warehouseId as number) : undefined,
      serviceChargePercent: formData.serviceChargePercent,
      deliveryCharge: formData.deliveryCharge,
      dayCloseCutoff: formData.dayCloseCutoff.trim() || null,
      isActive: formData.isActive,
    };
    try {
      if (isEdit && id) {
        await outletService.update(parseInt(id), payload);
      } else {
        await outletService.create(payload);
      }
      navigate('/restaurant/outlets');
    } catch (err: any) {
      const parsed = parseApiError(err);
      if (parsed.code === 'LICENSE_LIMIT_OUTLETS') {
        setLimitReached(parsed.message);
      } else {
        setError(getServerErrorMessage(err) ?? t('restaurant.outlet.failedToSave'));
      }
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="ims-page rest-page" style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="rest-create-page">
        <div className="ims-create-body-page">
          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <button
                type="button"
                className="ims-back-btn"
                onClick={() => navigate('/restaurant/outlets')}
              >
                <ArrowLeft size={14} />
                {t('restaurant.outlet.title')}
              </button>
              <div>
                <h1 className="ims-page-title">
                  {isEdit ? t('restaurant.outlet.editTitle') : t('restaurant.outlet.createTitle')}
                </h1>
                <p className="ims-page-subtitle">
                  {isEdit
                    ? 'Update the outlet details below'
                    : 'Fill in the details to register a new outlet'}
                </p>
              </div>
            </div>
            <div className="ims-top-bar-right">
              <button
                type="button"
                className="ims-btn ims-btn-secondary"
                onClick={() => navigate('/restaurant/outlets')}
                disabled={loading}
              >
                {t('common.cancel')}
              </button>
              <button type="submit" className="ims-btn ims-btn-primary" disabled={loading}>
                {loading ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>

          {error && (
            <div className="ims-error-banner">
              <div className="ims-alert ims-alert-error">
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            </div>
          )}

          {limitReached && (
            <div
              className="ims-alert"
              style={{
                display: 'flex', gap: 12, alignItems: 'flex-start',
                border: '1px solid var(--ims-border)', borderRadius: 10,
                padding: '14px 16px', margin: '0 0 12px',
                background: 'var(--ims-surface-2, #f9fafb)',
              }}
            >
              <Lock size={18} style={{ marginTop: 2, flexShrink: 0, color: 'var(--ims-accent, #059669)' }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {t('restaurant.outlet.limitTitle', 'Outlet limit reached')}
                </div>
                <div style={{ fontSize: 13, color: 'var(--ims-text-sub)' }}>
                  {limitReached}{' '}
                  {t('restaurant.outlet.limitUpgradeHint',
                    'Your plan includes one outlet. Contact your administrator to unlock more.')}
                </div>
              </div>
            </div>
          )}

          <div className="ims-create-body ims-create-body-two">
            <div className="ims-panel-left">
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <Store size={15} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    {t('restaurant.outlet.details')}
                  </span>
                </div>
                <div className="ims-card-body">
                  <div className="ims-form-row">
                    <div className="ims-form-group">
                      <label className="ims-form-label">
                        {t('restaurant.outlet.code')}{' '}
                        <span style={{ color: 'var(--ims-red-text)' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="code"
                        className={`ims-form-control${fieldErrors.code ? ' is-invalid' : ''}`}
                        value={formData.code}
                        onChange={handleChange}
                        placeholder={t('restaurant.outlet.codePlaceholder')}
                        maxLength={20}
                        style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                      />
                      {fieldErrors.code && (
                        <small className="ims-field-error">{fieldErrors.code}</small>
                      )}
                    </div>

                    <div className="ims-form-group">
                      <label className="ims-form-label">
                        {t('restaurant.outlet.name')}{' '}
                        <span style={{ color: 'var(--ims-red-text)' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        className={`ims-form-control${fieldErrors.name ? ' is-invalid' : ''}`}
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('restaurant.outlet.namePlaceholder')}
                        maxLength={150}
                      />
                      {fieldErrors.name && (
                        <small className="ims-field-error">{fieldErrors.name}</small>
                      )}
                    </div>
                  </div>

                  <div className="ims-form-row">
                    <div className="ims-form-group">
                      <label className="ims-form-label">{t('restaurant.outlet.phone')}</label>
                      <input
                        type="text"
                        name="phone"
                        className="ims-form-control"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('restaurant.outlet.phonePlaceholder')}
                        maxLength={50}
                      />
                    </div>

                    <div className="ims-form-group">
                      <label className="ims-form-label">{t('restaurant.outlet.email')}</label>
                      <input
                        type="email"
                        name="email"
                        className="ims-form-control"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('restaurant.outlet.emailPlaceholder')}
                        maxLength={150}
                      />
                    </div>
                  </div>

                  <div className="ims-form-group">
                    <label className="ims-form-label">{t('restaurant.outlet.warehouse')}</label>
                    <select
                      name="warehouseId"
                      className="ims-form-control"
                      value={formData.warehouseId}
                      onChange={handleChange}
                    >
                      <option value="">{t('restaurant.outlet.noWarehouse')}</option>
                      {warehouses.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}{w.code ? ` (${w.code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="ims-form-group ims-form-check">
                    <input
                      type="checkbox"
                      name="isActive"
                      id="isActive"
                      className="ims-check-input"
                      checked={formData.isActive}
                      onChange={handleChange}
                    />
                    <label htmlFor="isActive" className="ims-check-label">
                      {t('common.active')}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="ims-panel-right">
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <MapPin size={15} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    Location &amp; Notes
                  </span>
                </div>
                <div className="ims-card-body">
                  <div className="ims-form-group">
                    <label className="ims-form-label">{t('restaurant.outlet.address')}</label>
                    <input
                      type="text"
                      name="address"
                      className="ims-form-control"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder={t('restaurant.outlet.addressPlaceholder')}
                      maxLength={300}
                    />
                  </div>

                  <div className="ims-form-group">
                    <label className="ims-form-label">{t('common.description')}</label>
                    <textarea
                      name="description"
                      className="ims-form-control"
                      value={formData.description}
                      onChange={handleChange}
                      rows={4}
                      placeholder={t('restaurant.outlet.descriptionPlaceholder')}
                      maxLength={500}
                    />
                  </div>
                </div>
              </div>

              <div className="ims-card" style={{ marginTop: 14 }}>
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <Receipt size={15} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    {t('restaurant.outlet.billing', 'Billing')}
                  </span>
                </div>
                <div className="ims-card-body">
                  <div className="ims-form-row">
                    <div className="ims-form-group">
                      <label className="ims-form-label">{t('restaurant.outlet.serviceChargePercent', 'Service Charge (%)')}</label>
                      <input
                        type="number"
                        name="serviceChargePercent"
                        className="ims-form-control"
                        value={formData.serviceChargePercent}
                        onChange={handleChange}
                        min={0} max={100} step="0.01" placeholder="0"
                      />
                      <small style={{ display: 'block', color: 'var(--ims-text-sub)', marginTop: 4 }}>{t('restaurant.outlet.serviceChargeHint', 'Applied to every order placed for this outlet')}</small>
                    </div>

                    <div className="ims-form-group">
                      <label className="ims-form-label">{t('restaurant.outlet.deliveryCharge', 'Delivery Charge')}</label>
                      <input
                        type="number"
                        name="deliveryCharge"
                        className="ims-form-control"
                        value={formData.deliveryCharge}
                        onChange={handleChange}
                        min={0} step="0.01" placeholder="0.00"
                      />
                      <small style={{ display: 'block', color: 'var(--ims-text-sub)', marginTop: 4 }}>{t('restaurant.outlet.deliveryChargeHint', 'Flat fee added only to Delivery orders')}</small>
                    </div>
                  </div>

                  <div className="ims-form-row">
                    <div className="ims-form-group">
                      <label className="ims-form-label">{t('restaurant.outlet.dayCloseCutoff', 'Day-End Cutoff Time')}</label>
                      <input
                        type="time"
                        name="dayCloseCutoff"
                        className="ims-form-control"
                        value={formData.dayCloseCutoff}
                        onChange={handleChange}
                      />
                      <small style={{ display: 'block', color: 'var(--ims-text-sub)', marginTop: 4 }}>
                        {t('restaurant.outlet.dayCloseCutoffHint', 'After this time, sales are blocked until the previous day is closed. Allows trading past midnight up to this time. Leave blank to disable.')}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default OutletCreatePage;
